import React, { useCallback, useState } from 'react'

interface FileUploadProps {
  onFileSelect?: (files: FileList) => void
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect }) => {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      if (e.dataTransfer.files && onFileSelect) {
        onFileSelect(e.dataTransfer.files)
      }
    },
    [onFileSelect]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && onFileSelect) {
        onFileSelect(e.target.files)
      }
    },
    [onFileSelect]
  )

  return (
    <div
      className={`position-relative border border-2 border-dashed rounded-4 p-5 ${
        isDragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary bg-white'
      }`}
      style={{
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type='file'
        multiple
        className='position-absolute top-0 start-0 w-100 h-100 opacity-0'
        style={{ cursor: 'pointer' }}
        onChange={handleFileInput}
        accept='.svg,.jpg,.jpeg,.png,.pdf'
      />

      <div className='d-flex align-items-center justify-content-between'>
        <div className='flex-grow-1 d-flex flex-column align-items-center'>
          {/* Upload Icon */}
          <div className='mb-3'>
            <svg width='48' height='48' fill='none' stroke='#6366F1' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          </div>

          {/* Text */}
          <p className='text-center mb-1'>
            <span className='fw-medium' style={{ color: '#6366F1' }}>
              Click here
            </span>
            <span className='text-secondary'> to upload your file or drag.</span>
          </p>
          <p className='text-muted small mb-0'>Supported Format: SVG, JPG, PNG (10mb each)</p>
        </div>

        {/* PDF Icon on right */}
        <div className='d-none d-md-flex flex-column align-items-center ms-4'>
          <div className='position-relative'>
            <svg width='64' height='64' fill='none' stroke='#dee2e6' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1}
                d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z'
              />
            </svg>
            {/* Hand cursor icon */}
            <svg
              className='position-absolute'
              style={{ top: '-8px', right: '-8px' }}
              width='32'
              height='32'
              fill='none'
              stroke='#adb5bd'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11'
              />
            </svg>
          </div>
          <span className='text-muted small fw-medium mt-2'>PDF</span>
        </div>
      </div>
    </div>
  )
}

export default FileUpload
